<template>
  <div
    class="sam-radio"
    :class="RadioClassObj"
    data-samora
  >
    <div
      v-for="(opt, index) in radioItems"
      :key="index"
      class="sam-radio__item"
      data-testid="sam-radio__item"
    >
      <button
        :ref="`${radioId}button${index}`"
        :disabled="disabled || opt.disabled"
        type="button"
        class="sam-radio__btn"
        @keydown.up.down.prevent
        @mousedown="handleKeyup(`${radioId}input${index}`)"
        @keyup.enter.space="handleKeyup(`${radioId}input${index}`)"
        @keyup.left.up="handleKeyNav(true, index)"
        @keyup.right.down="handleKeyNav(false, index)"
      >
        <input
          :ref="`${radioId}input${index}`"
          v-model="value"
          class="sam-radio__input"
          type="radio"
          :value="opt.value"
          :disabled="disabled"
          @change="selectOption"
        />
        <label
          class="sam-radio__content"
          :for="`${radioId}input${index}`"
        >
          <span class="d-block">
            <span class="sam-radio__label">{{ opt.label }}</span>
          </span>
        </label>
      </button>
      <span
        v-if="hasCaption(opt.caption)"
        class="sam-radio__caption"
      >
        {{ opt.caption }}
      </span>
      <div class="sam-radio__slot">
        <slot
          :name="`radio-${index}`"
          v-bind="{ disabled }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { randomizeId } from '../../../utils/utils';

export default {
  name: 'SamRadioGroup',
  model: {
    prop: 'data',
    event: 'change',
  },
  props: {
    /**
     * The Radio group options
     */
    radioItems: {
      type: Array,
      required: false,
      default: () => [],
      validator: (data) => {
        const conditions = [];
        data.forEach((item) => {
          conditions.push(Object.keys(item).indexOf('label'));
          conditions.push(Object.keys(item).indexOf('value'));
        });

        return data.length === 0 || conditions.every((cond) => cond > -1);
      },
    },
    /**
     * The Radio data variable
     */
    data: {
      type: [Boolean, String, Number],
      required: false,
      default: '',
    },
    /**
     * If the Radio input is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Controls how the Radio items are arranged on the container
     * @values flex-start, flex-end, center, space-between, space-around, space-evenly, equal-spacing-align-left, equal-spacing-align-right
     */
    itemsAlignment: {
      type: String,
      required: false,
      default: 'flex-start',
      validator(type) {
        return [
          'flex-start',
          'flex-end',
          'center',
          'space-between',
          'space-around',
          'space-evenly',
          'equal-spacing-align-left',
          'equal-spacing-align-right',
        ].includes(type);
      },
    },
    /**
     * Sets the flex direction for the Radio buttons list
     * @values horizontal, vertical
     */
    layout: {
      type: String,
      required: false,
      default: 'horizontal',
      validator(layout) {
        return ['horizontal', 'vertical'].includes(layout);
      },
    },
  },
  data() {
    return {
      value: '',
      radioId: randomizeId('radioid'),
    };
  },
  computed: {
    RadioClassObj() {
      return {
        [this.itemsAlignment]: true,
        'is-vertical': this.layout === 'vertical',
        disabled: this.disabled,
      };
    },
  },
  watch: {
    data: {
      handler(newVal) {
        this.value = newVal;
      },
      immediate: true,
    },
  },
  methods: {
    handleKeyup(ref) {
      this.$refs[ref][0].click();
    },
    handleKeyNav(moveLeft, index) {
      let finalRef = `${this.radioId}button`;

      if (moveLeft) {
        finalRef =
          index === 0 ? `${finalRef}${this.radioItems.length - 1}` : `${finalRef}${index - 1}`;
      } else {
        finalRef =
          index === this.radioItems.length - 1 ? `${finalRef}0` : `${finalRef}${index + 1}`;
      }

      // eslint-disable-next-line no-unused-expressions
      this.$refs?.[finalRef]?.[0]?.focus?.();
    },
    selectOption() {
      /**
       * Triggers when Radio changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
      this.$emit('change', this.value);
    },
    hasCaption(caption) {
      return String(caption ?? '').length;
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-radio {
  @extend .d-flex;
  gap: $size--4 $size--8;

  &.is-vertical {
    flex-direction: column;
    align-items: flex-start;

    .sam-radio__item {
      width: 100%;
    }
  }

  &.flex-start {
    justify-content: flex-start;
  }

  &.flex-end {
    justify-content: flex-end;
  }

  &.center {
    justify-content: center;
  }

  &.space-between {
    justify-content: space-between;
  }

  &.space-around {
    justify-content: space-around;
  }

  &.space-evenly {
    justify-content: space-evenly;
  }

  &.equal-spacing-align-left {
    .sam-radio__btn {
      width: 100%;
    }
  }

  &.equal-spacing-align-right {
    .sam-radio__btn {
      width: 100%;

      & label {
        display: flex;
        justify-content: flex-end;
      }

      & span {
        width: unset;
      }
    }
  }

  &__btn {
    @extend .m-0, .p-0;
    border: none;
    outline: none;
    background-color: transparent;
    position: relative;
    display: flex;

    &:focus-within {
      .sam-radio__content::before {
        outline: 3px solid $informative--border;
        border-color: $accent--unbabel-blue;
      }
    }
  }

  &:not(.is-vertical) &__btn {
    align-self: stretch;
  }

  &__input {
    width: 0;
    height: 0;
    opacity: 0;
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
  }

  &__content {
    text-align: left;
    width: 100%;
    height: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    position: relative;

    &::before {
      content: '';
      position: relative;
      width: 20px;
      height: 20px;
      border: 1px solid $neutral--90;
      border-radius: 20px;
      background-color: $pure--white;
      display: block;
      margin-right: $size--2;
    }

    &::after {
      content: '';
      width: 12px;
      height: 12px;
      border-radius: 14px;
      background-color: $accent--unbabel-blue;
      visibility: hidden;
      opacity: 0;
      position: absolute;
      left: 5px;
      transition: all $default-time $default-easing;
    }

    &:hover {
      &::before {
        border: 1px solid $informative--hover;
      }
    }
  }

  &__slot {
    margin-left: calc(23px + 0.5rem);
    text-align: left;
  }

  &__label {
    @extend .text--body-2;
  }

  &__caption {
    @extend .text--caption, .d-block;
    color: $neutral--80;
    margin-left: calc(
      $size--5 + $size--2 + $size--0--fourth
    ); // width, margin and border sizes of radio button
  }

  &__input:checked + &__content {
    .sam-radio__label {
      @extend .text--body-2--medium;
    }

    &::after {
      visibility: visible;
      opacity: 1;
    }
  }

  &__btn:disabled {
    .sam-radio__caption {
      color: $neutral--60;
    }

    .sam-radio__content {
      cursor: not-allowed;

      .sam-radio__label {
        color: $neutral--60;
      }

      &::before {
        border-color: $neutral--40;
      }

      &::after {
        background-color: $neutral--40;
      }
    }
  }
}
</style>
